import { ExtendedUserAuditLog } from "./types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { TFunction } from "i18next";
import { ChevronDown, Eye, Globe, Monitor, Trash2 } from "lucide-react";

export const getAuditLogColumns = (
  t: TFunction,
  onView: (log: ExtendedUserAuditLog) => void,
  onDelete: (id: string) => void,
): ColumnDef<ExtendedUserAuditLog>[] => {
  const getActionBadgeVariant = (
    action: string,
  ): "default" | "destructive" | "secondary" | "outline" => {
    switch (action) {
      case "UPDATE_PROFILE":
        return "default";
      case "DELETE_PROFILE":
        return "destructive";
      case "LOGIN":
        return "secondary";
      case "LOGOUT":
        return "outline";
      default:
        return "default";
    }
  };

  return [
    {
      accessorKey: "action",
      header: () => t("settings.auditLogs.columns.action"),
      cell: ({ row }) => {
        const action = row.getValue("action") as string;
        const hasChanges =
          row.original.details?.changes &&
          row.original.details.changes.length > 0;

        return (
          <div className="flex items-center gap-2">
            <Badge variant={getActionBadgeVariant(action)}>
              {t(`settings.auditLogs.actions.${action}`)}
            </Badge>
            {hasChanges && (
              <Badge variant="outline" className="cursor-help">
                {row.original.details.changes.length}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "ipAddress",
      header: () => t("settings.auditLogs.columns.ipAddress"),
      cell: ({ row }) => {
        const ipAddress = row.getValue("ipAddress") as string;
        return (
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span>{ipAddress || "N/A"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "userAgent",
      header: () => t("settings.auditLogs.columns.userAgent"),
      cell: ({ row }) => {
        const userAgent = row.getValue("userAgent") as string;
        return (
          <div className="flex items-center gap-2">
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <span className="truncate max-w-[200px]">{userAgent || "N/A"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: () => t("settings.auditLogs.columns.createdAt"),
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as string;
        return format(new Date(date), "d MMMM yyyy HH:mm");
      },
    },
    {
      id: "actions",
      header: () => t("settings.auditLogs.columns.actions"),
      cell: ({ row }) => {
        const log = row.original;
        const hasChanges =
          log.details?.changes && log.details.changes.length > 0;

        return (
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button variant="ghost" size="icon" onClick={() => onView(log)}>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {hasChanges && (
                  <>
                    <DropdownMenuItem onClick={() => onView(log)}>
                      <Eye className="mr-2 h-4 w-4" />
                      {t("settings.auditLogs.details.viewChanges")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => onDelete(log.id)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("settings.auditLogs.actions.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];
};
